import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import type {
  CompradorPessoaFisicaStepSchemaOutput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { CompradorPessoaJuridicaSchemaOutput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pj.schema'
import type { TClienteCompleto } from '@/features/proposta/brasil/hooks/use-orquestrar-criacao-edicao'
import { CadastroCompradorPF } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/cadastro-comprador-pessoa-fisica'
import { CadastroCompradorPJ } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/cadastro-comprador-pessoa-juridica'
import { CadastroMaePaiFluxoIndependente } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/cadastro-mae-pai-fluxo-independente'
import { CadastroProcuradorFluxoIndependente } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/cadastro-procurador-fluxo-independente'
import { CadastroRepresentanteLegalFluxoIndependente } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/cadastro-representante-legal-fluxo-independente'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import type { TProfissao } from '@/types/cliente'

type TFluxosCadastroCompradorProps = {
  flowKey: 'PessoaFisica' | 'PessoaJuridica' | 'Procurador' | 'MaePai' | 'Representante' | 'Conjuge' | null
  mode: 'create' | 'edit'
  profissoes: TProfissao[]
  clientes: TPropostaSchemaInput['cadastroCompradores']['clientes']
  compradores: TCompradoresCadastrados
  procuradores: TPropostaSchemaInput['cadastroCompradores']['procuradores']
  clientesJuridicos: TPropostaSchemaInput['cadastroCompradores']['clientesJuridicos']
  clienteToEdit: TClienteCompleto | null
  emailsJaVinculados: Record<string, string>
  clienteAtendimento?: { id: string; cpf?: string | null }
  onCancel: () => void
  onSubmit: (
    comprador:
      | CompradorPessoaFisicaStepSchemaOutput
      | CompradorPessoaJuridicaSchemaOutput
      | CompradorVinculoStepSchemaOutput,
    isDirty: boolean,
  ) => Promise<void>
}

export const FluxosCadastroComprador = ({
  flowKey,
  mode,
  profissoes,
  clientes,
  compradores,
  procuradores,
  clientesJuridicos,
  clienteToEdit,
  emailsJaVinculados,
  clienteAtendimento,
  onCancel,
  onSubmit,
}: TFluxosCadastroCompradorProps) => {
  if (flowKey === 'PessoaFisica') {
    return (
      <CadastroCompradorPF
        profissoes={profissoes}
        clientes={clientes}
        compradores={compradores}
        clienteAtendimento={clienteAtendimento}
        clienteToEdit={clienteToEdit}
        mode={mode}
        onCancel={onCancel}
        onSubmit={onSubmit}
        emailsJaVinculados={emailsJaVinculados}
      />
    )
  }

  if (flowKey === 'PessoaJuridica') {
    return (
      <CadastroCompradorPJ
        compradores={compradores}
        profissoes={profissoes}
        clienteJuridicoToEdit={clienteToEdit}
        mode={mode}
        onCancel={onCancel}
        onSubmit={onSubmit}
        emailsJaVinculados={emailsJaVinculados}
      />
    )
  }

  if (flowKey === 'Procurador') {
    return (
      <CadastroProcuradorFluxoIndependente
        procuradorToEdit={clienteToEdit}
        compradores={compradores}
        clientes={clientes}
        procuradores={procuradores}
        profissoes={profissoes}
        onCancel={onCancel}
        onSubmit={onSubmit}
        emailsJaVinculados={emailsJaVinculados}
      />
    )
  }

  if (flowKey === 'MaePai') {
    return (
      <CadastroMaePaiFluxoIndependente
        emailsJaVinculados={emailsJaVinculados}
        clienteToEdit={clienteToEdit}
        profissoes={profissoes}
        compradores={compradores}
        clientes={clientes}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    )
  }

  if (flowKey === 'Representante') {
    return (
      <CadastroRepresentanteLegalFluxoIndependente
        pjToEdit={clienteToEdit}
        profissoes={profissoes}
        compradores={compradores}
        clientesJuridicos={clientesJuridicos}
        emailsJaVinculados={emailsJaVinculados}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    )
  }

  return null
}
